import React, { useState } from 'react';
import {
  Modal,
  PrimaryButton,
  SecondaryButton,
  SkeletonLoader,
  Text,
} from '@weave/design-system';
import { ReportEntityTagHistoryRequest_Format } from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';
import { useQueries } from 'react-query';
import { useInfiniteReportEntityTagHistoryQuery } from 'apis/wetags/queries';
import { EntityTagHistoryContent } from './EntityTagHistoryContent.component';
import { SegmentedControl } from './EntityTagHistoryTimeline.component';
import { contextChipClasses, modalClasses } from './EntityTagHistory.styles';
import type {
  ActorNames,
  ContextChipProps,
  EntityTagHistoryModalProps,
} from './entityTagHistory.types';
import { searchUserById } from 'components/user-look-up/user-search.api';

const formatDescriptions: Record<ReportEntityTagHistoryRequest_Format, string> = {
  [ReportEntityTagHistoryRequest_Format.FORMAT_UNSPECIFIED]: '',
  [ReportEntityTagHistoryRequest_Format.FORMAT_COMPLETE]:
    'Every recorded event, including ones that did not change whether the entity is tagged.',
  [ReportEntityTagHistoryRequest_Format.FORMAT_MATERIAL]:
    'Only events that changed the tag check result for this entity.',
};

export function EntityTagHistoryModal({
  namespaceId,
  tagId,
  entityId,
  onClose,
  showVisualization = true,
}: EntityTagHistoryModalProps) {
  const [format, setFormat] = useState(
    ReportEntityTagHistoryRequest_Format.FORMAT_MATERIAL
  );
  const historyQuery = useInfiniteReportEntityTagHistoryQuery({
    namespaceId,
    tagId,
    entityId,
    format,
    enabled: true,
  });
  const events = historyQuery.data?.pages.flatMap((page) => page.events ?? []) ?? [];
  const actorIds = Array.from(
    new Set(events.flatMap((event) => (event.actorId ? [event.actorId] : [])))
  );
  const actorQueries = useQueries(
    actorIds.map((actorId) => ({
      queryKey: ['wetags-history-actor', actorId],
      queryFn: async () => {
        const user = await searchUserById(actorId);
        const name = [user?.FirstName, user?.LastName].filter(Boolean).join(' ');

        if (!name) {
          throw new Error('No name returned for tag history actor');
        }
        return { name, username: user?.Username || undefined };
      },
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      onError: (error: unknown) =>
        console.error('Error resolving tag history actor:', actorId, error),
    }))
  );
  const actorNames: ActorNames = {};
  actorQueries.forEach((query, index) => {
    const data: unknown = query.data;
    if (
      typeof data === 'object' &&
      data !== null &&
      'name' in data &&
      typeof data.name === 'string'
    ) {
      actorNames[actorIds[index]] = {
        name: data.name,
        username:
          'username' in data && typeof data.username === 'string'
            ? data.username
            : undefined,
      };
    }
  });
  const hasActorErrors = actorQueries.some((query) => query.isError);
  const isResolvingActors = actorQueries.some((query) => query.isLoading);
  const showSkeleton =
    (historyQuery.isLoading && !historyQuery.data) || isResolvingActors;
  const statusMessage = isResolvingActors
    ? 'Resolving actor names...'
    : historyQuery.isFetchingNextPage
    ? 'Loading more history...'
    : historyQuery.isFetching
    ? 'Loading tag history...'
    : undefined;

  return (
    <Modal show onClose={onClose} maxWidth={1400}>
      <Modal.Header onClose={onClose}>Entity Tag History</Modal.Header>
      <Modal.Body>
        <div className={modalClasses.content}>
          <div className={modalClasses.toolbar}>
            <div className={modalClasses.contextChips}>
              <ContextChip label="Namespace" value={namespaceId} />
              <ContextChip label="Tag" value={tagId} />
              <ContextChip label="Entity ID" value={entityId} />
            </div>
            <SegmentedControl
              label="History view"
              value={format}
              onChange={setFormat}
              options={[
                {
                  value: ReportEntityTagHistoryRequest_Format.FORMAT_MATERIAL,
                  label: 'Material changes',
                },
                {
                  value: ReportEntityTagHistoryRequest_Format.FORMAT_COMPLETE,
                  label: 'Full history',
                },
              ]}
            />
          </div>
          <Text size="small" className={modalClasses.formatDescription}>
            {formatDescriptions[format]}
          </Text>

          {statusMessage && (
            <Text role="status" size="small" className={modalClasses.statusMessage}>
              {statusMessage}
            </Text>
          )}

          {historyQuery.isError && (
            <div role="alert" className={modalClasses.errorAlert}>
              <Text color="error" className={modalClasses.errorMessage}>
                Unable to load tag history. Please try again.
              </Text>
              <SecondaryButton
                type="button"
                disabled={historyQuery.isFetching}
                onClick={() => void historyQuery.refetch()}
                className={modalClasses.actionButton}
              >
                Retry
              </SecondaryButton>
            </div>
          )}

          {hasActorErrors && !isResolvingActors && (
            <Text
              color="error"
              role="alert"
              size="small"
              className={modalClasses.actorWarning}
            >
              Some actor names could not be loaded. Their actor IDs are shown instead.
            </Text>
          )}

          {showSkeleton ? (
            <div aria-hidden>
              <SkeletonLoader
                animation="shimmer"
                width="100%"
                height={96}
                count={1}
                distance={12}
              />
              <SkeletonLoader
                animation="shimmer"
                width="100%"
                height={140}
                count={1}
                distance={12}
              />
              <SkeletonLoader
                animation="shimmer"
                width="100%"
                height={32}
                count={5}
                distance={8}
              />
            </div>
          ) : (
            historyQuery.data && (
              <EntityTagHistoryContent
                events={events}
                actorNames={actorNames}
                isComplete={!historyQuery.hasNextPage}
                showVisualization={showVisualization}
              />
            )
          )}

          <div className={modalClasses.footer}>
            {historyQuery.hasNextPage ? (
              <PrimaryButton
                type="button"
                disabled={historyQuery.isFetching || isResolvingActors}
                onClick={() => void historyQuery.fetchNextPage()}
                className={modalClasses.actionButton}
              >
                Load more
              </PrimaryButton>
            ) : (
              <span />
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function ContextChip({ label, value }: ContextChipProps) {
  return (
    <div className={contextChipClasses.root}>
      <dt className={contextChipClasses.label}>{label}:</dt>
      <dd title={value} className={contextChipClasses.value}>
        {value}
      </dd>
    </div>
  );
}
